import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth-context'
import { useWorkspace } from '@/lib/workspace-context'
import { useToast } from '@lead-magnifier/ui'
import type { RealtimeChannel } from '@supabase/supabase-js'

interface RealtimeLead {
  id: string
  email: string
  name: string | null
  event_id: string
  tenant_id: string
  doi_confirmed: boolean
  created_at: string
}

export function useRealtimeLeads() {
  const { user } = useAuth()
  const { activeWorkspace } = useWorkspace()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const channelRef = useRef<RealtimeChannel | null>(null)

  useEffect(() => {
    if (!user || !activeWorkspace) return

    // Clean up previous subscription
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current)
    }

    // Subscribe to INSERT events on leads table for this workspace
    const channel = supabase
      .channel(`leads-${activeWorkspace.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'leads',
          filter: `tenant_id=eq.${activeWorkspace.id}`,
        },
        async (payload) => {
          const lead = payload.new as RealtimeLead

          // Get event name for the toast
          const { data: event } = await supabase
            .from('events')
            .select('name')
            .eq('id', lead.event_id)
            .single()

          // Show toast notification
          toast({
            title: 'Neuer Lead!',
            description: `${lead.email}${event?.name ? ` bei "${event.name}"` : ''}`,
          })

          // Invalidate queries to refresh data
          queryClient.invalidateQueries({ queryKey: ['leads'] })
          queryClient.invalidateQueries({ queryKey: ['lead-stats'] })
          queryClient.invalidateQueries({ queryKey: ['events'] })
          queryClient.invalidateQueries({ queryKey: ['event', lead.event_id] })
          queryClient.invalidateQueries({ queryKey: ['event-leads', lead.event_id] })
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'leads',
          filter: `tenant_id=eq.${activeWorkspace.id}`,
        },
        async (payload) => {
          const lead = payload.new as RealtimeLead
          const oldLead = payload.old as RealtimeLead

          // Check if DOI was just confirmed
          if (lead.doi_confirmed && !oldLead.doi_confirmed) {
            // Get event name
            const { data: event } = await supabase
              .from('events')
              .select('name')
              .eq('id', lead.event_id)
              .single()

            toast({
              title: 'Lead bestätigt!',
              description: `${lead.email} hat Double Opt-In bestätigt${event?.name ? ` für "${event.name}"` : ''}`,
            })
          }

          // Invalidate queries
          queryClient.invalidateQueries({ queryKey: ['leads'] })
          queryClient.invalidateQueries({ queryKey: ['lead-stats'] })
          queryClient.invalidateQueries({ queryKey: ['events'] })
          queryClient.invalidateQueries({ queryKey: ['event', lead.event_id] })
          queryClient.invalidateQueries({ queryKey: ['event-leads', lead.event_id] })
        }
      )
      .subscribe()

    channelRef.current = channel

    // Cleanup subscription on unmount or workspace change
    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current)
      }
    }
  }, [user, activeWorkspace?.id, toast, queryClient])
}
